"use client";

import { useState, useEffect, useCallback } from "react";
import { ontologyGetLakehouseStats } from "../sdk";

interface LakehouseStats {
  totalTables: number;
  totalDataVolume: string;
  activeMigrations: number;
  queriesToday: number;
  recentActivity: ActivityItem[];
  datasourcesMigrated: number;
  semanticObjects: number;
  enhancedMetrics: number;
}

interface ActivityItem {
  id: string;
  type: "success" | "warning" | "info" | "error";
  message: string;
  details: string;
  timestamp: string;
}

interface UseLakehouseStatsResult {
  stats: LakehouseStats | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for getting lakehouse dashboard statistics and activity
 * @param orgId - Organization ID
 * @returns Lakehouse stats and operations
 */
export function useLakehouseStats(orgId: string): UseLakehouseStatsResult {
  const [stats, setStats] = useState<LakehouseStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!orgId) return;

    try {
      setLoading(true);
      setError(null);

      // Get real lakehouse statistics from backend
      const response = await ontologyGetLakehouseStats({
        query: { org_id: orgId },
      });

      const statsResponse = response.data;
      if (!statsResponse) {
        throw new Error("No data in response");
      }

      setStats({
        totalTables: statsResponse.total_tables || 0,
        totalDataVolume: statsResponse.total_data_volume || "0 B",
        activeMigrations: statsResponse.active_migrations || 0,
        queriesToday: statsResponse.queries_today || 0,
        recentActivity: (statsResponse.recent_activity || []).map(
          (activity: any) => ({
            id: activity.id,
            type: activity.type as "success" | "warning" | "info" | "error",
            message: activity.message,
            details: activity.details,
            timestamp: activity.timestamp,
          })
        ),
        datasourcesMigrated: statsResponse.datasources_migrated || 0,
        semanticObjects: statsResponse.semantic_objects || 0,
        enhancedMetrics: statsResponse.enhanced_metrics || 0,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch lakehouse stats"
      );
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const refetch = useCallback(async () => {
    await fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch,
  };
}

// Helper function to format file sizes
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}
